import {useApolloClient, useQuery} from '@apollo/client'
import {useEffect, useMemo} from 'react'
import {format, differenceInYears} from 'date-fns'
import {GET_BIRTHDAYS_BY_MONTH} from '../gql/queries/birthdaysQuery'
import {BirthdaysQueryResponse, BirthdayMember, BirthdaysByDate} from '../types/birthdays'

interface UseBirthdaysOptions {
  skip?: boolean
  effectiveYear?: number | null
  memberType?: 'HOLDER' | 'FAMILY' | null
}

export const useBirthdays = (month: number, options?: UseBirthdaysOptions) => {
  const client = useApolloClient()
  const skip = options?.skip ?? false
  const effectiveYear = options?.effectiveYear ?? null
  const memberType = options?.memberType ?? null
  const {data, loading, error} = useQuery<BirthdaysQueryResponse>(GET_BIRTHDAYS_BY_MONTH, {
    variables: {
      month,
      effectiveYear: effectiveYear ?? undefined,
      memberType: memberType ?? undefined,
    },
    skip,
    fetchPolicy: 'cache-and-network',
  })

  const transformContacts = (contacts?: BirthdaysQueryResponse['birthdaysByMonth']['contacts']) => {
    if (!contacts) return {}

    // Deduplicate: same first name + last name + birth date
    const seen = new Set<string>()
    const uniqueContacts = contacts.filter((contact) => {
      const key = [
        contact.firstName?.trim().toLowerCase() || '',
        contact.lastName?.trim().toLowerCase() || '',
        contact.dateBirth || '',
      ].join('|')
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })

    const groupedByDate: BirthdaysByDate = {}

    uniqueContacts.forEach((contact) => {
      if (!contact.dateBirth) return

      // Parsear como fecha local para evitar desfases por zona horaria
      const birthDate = (() => {
        const parts = contact.dateBirth.split('-').map(Number)
        if (parts.length >= 3 && !parts.some((n) => Number.isNaN(n))) {
          const [year, month, day] = parts
          return new Date(year, month - 1, day)
        }
        const parsed = new Date(contact.dateBirth)
        return Number.isNaN(parsed.getTime()) ? null : parsed
      })()

      if (!birthDate) return

      const age = differenceInYears(new Date(), birthDate)

      // Agrupar por día del mes (formato: "MM-DD")
      const monthDay = format(birthDate, 'MM-dd')

      const chosenAgent = contact.memberPolicy?.policy?.user || contact.user

      const isHolder =
        contact.memberPolicy?.rol === 'HOLDER' ||
        contact.members?.edges?.some((e) => e?.node?.rol === 'HOLDER') === true
      const memberRol: 'HOLDER' | 'FAMILY' = isHolder ? 'HOLDER' : 'FAMILY'

      const birthdayMember: BirthdayMember = {
        id: contact.id,
        firstName: contact.firstName,
        middleName: contact.middleName,
        lastName: contact.lastName,
        secondLastName: contact.secondLastName,
        dateBirth: contact.dateBirth,
        phone: contact.phone,
        age,
        agentName: chosenAgent
          ? `${chosenAgent.firstName ?? ''} ${chosenAgent.lastName ?? ''}`.trim() || undefined
          : undefined,
        agentId: chosenAgent?.id,
        agentNpn: chosenAgent?.npn,
        memberRol,
      }

      if (!groupedByDate[monthDay]) {
        groupedByDate[monthDay] = []
      }

      groupedByDate[monthDay].push(birthdayMember)
    })

    // Ordenar alfabéticamente por nombre y apellido dentro de cada día
    Object.keys(groupedByDate).forEach((monthDay) => {
      groupedByDate[monthDay].sort((a, b) => {
        // Comparar por firstName
        const firstNameCompare = a.firstName.localeCompare(b.firstName)
        if (firstNameCompare !== 0) return firstNameCompare

        // Si firstName es igual, comparar por lastName
        return a.lastName.localeCompare(b.lastName)
      })
    })

    return groupedByDate
  }

  const birthdaysByDate = useMemo<BirthdaysByDate>(() => {
    return transformContacts(data?.birthdaysByMonth?.contacts)
  }, [data])

  useEffect(() => {
    if (skip) return
    if (data?.birthdaysByMonth?.totalCount != null) {
      // Log para depuración: total y flag parcial
      // eslint-disable-next-line no-console
      console.log(
        '[Birthdays]',
        'total:',
        data.birthdaysByMonth.totalCount,
        'isPartial:',
        data.birthdaysByMonth.isPartial
      )
    }
  }, [data])

  const loadDayBirthdays = async (day: number) => {
    if (skip) return []
    const res = await client.query<BirthdaysQueryResponse>({
      query: GET_BIRTHDAYS_BY_MONTH,
      variables: {
        month,
        day,
        effectiveYear: effectiveYear ?? undefined,
        memberType: memberType ?? undefined,
      },
      fetchPolicy: 'network-only',
    })
    const contacts = res.data.birthdaysByMonth?.contacts
    const grouped = transformContacts(contacts)
    const monthDayKey = `${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
    return grouped[monthDayKey] || []
  }

  return {
    birthdaysByDate: skip ? {} : birthdaysByDate,
    loading: skip ? false : loading,
    error,
    isPartial: skip ? false : data?.birthdaysByMonth?.isPartial ?? false,
    loadDayBirthdays,
  }
}
